import { useMemo } from 'react'

import {
  ClientError,
  isForbidden,
  isNotFound,
} from '@tribeplatform/gql-client/lib'
import {
  PermissionContext,
  Post,
  PostStatus,
} from '@tribeplatform/gql-client/types'
import { dayjs } from '@tribeplatform/react-components/common/lib'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { PostCardLoading } from '@tribeplatform/react-components/LoadingStates'
import { convertToStatic } from '@tribeplatform/react-components/Views'
import {
  useAuthMember,
  useNetwork,
  usePost,
} from '@tribeplatform/react-sdk/hooks'

import { PostsCardItem } from '../../PostsBlock/components/PostsCardItem.js'
import { useCustomFields } from '../../PostsBlock/hooks/useCustomFields.js'
import { staticFieldsFactory } from '../config.js'
import type { SinglePostBlockProps } from '../types.js'
import { PostNotFound } from './PostNotFound.js'

interface Props {
  postId: string
  fields: SinglePostBlockProps['fields']
  postTypeId: string
  postTypeSlug: string
  activeFieldId: string
  isPostPage?: boolean
  preview: boolean
}
export const PostCard = ({
  postId,
  fields,
  postTypeId,
  postTypeSlug,
  activeFieldId,
  isPostPage,
  preview,
  ...rest
}: Props) => {
  const {
    data: { postTypes },
  } = useNetwork()
  const { isModerator, data: authMember } = useAuthMember()
  const {
    data: fetchedPost,
    error: _error,
    isInitialLoading,
  } = usePost({
    variables: { id: postId },
    fields: 'default',
    useQueryOptions: {
      refetchOnMount: 'always',
      retry: false,
      enabled: !!postId,
    },
  })

  const staticFields = useStaticIntl(staticFieldsFactory)
  const postType = postTypes?.find(
    postType => postType.id === postTypeId || postType.slug === postTypeSlug,
  )
  const { customFields } = useCustomFields({
    postTypeSlugs:
      preview && !postId ? [postType.slug] : [fetchedPost?.postType?.slug],
    excludeRichText: false,
    excludeTitle: false,
    excludePrivateFields: false,
  })
  const post = useMemo(() => {
    if (preview && !postId) {
      return {
        postTypeId,
        postType: { id: postTypeId, slug: postTypeSlug },
        status: PostStatus.PUBLISHED,
        owner: { member: authMember },
        primaryReactionType: postType?.primaryReactionType,
        relativeUrl: '/',
        fields: [{ key: 'title', value: '"Title"' }],
        space: {
          name: 'Space',
          slug: 'space',
        },
        authMemberProps: {
          context: PermissionContext.POST,
          canReact: true,
          permissions: [
            {
              name: 'addReaction',
              isAuthorized: { authorized: true },
              inputPermissions: [],
              outputPermissions: [],
            },
            {
              name: 'updatePost',
              isAuthorized: { authorized: true },
              inputPermissions: [],
              outputPermissions: [],
            },
            {
              name: 'createReply',
              isAuthorized: { authorized: true },
              inputPermissions: [],
              outputPermissions: [],
            },
          ],
        },
        publishedAt: dayjs().subtract(5, 'day').toDate(),
      } as Post
    }

    return fetchedPost
  }, [
    authMember,
    fetchedPost,
    postId,
    postType?.primaryReactionType,
    postTypeId,
    postTypeSlug,
    preview,
  ])
  const error = _error as ClientError

  if (!post && !isInitialLoading && error) {
    if (isNotFound(error) || isForbidden(error)) {
      return <PostNotFound />
    }
  }

  if (
    post &&
    !isModerator &&
    post?.status !== PostStatus.PUBLISHED &&
    authMember?.id &&
    post?.owner?.member?.id !== authMember?.id
  ) {
    return <PostNotFound />
  }

  if (isInitialLoading || !post) {
    return <PostCardLoading />
  }

  return (
    <PostsCardItem
      post={post}
      fields={convertToStatic(fields, staticFields, customFields ?? []).filter(
        field =>
          field.enabled &&
          !(field.styles && field.styles.indexOf('card') === -1),
      )}
      preview={preview || !postId}
      activeFieldId={activeFieldId}
      isPostPage={isPostPage}
      isSinglePost
      {...rest}
    />
  )
}
